import React, { useState } from 'react';
import { X, Download, Share2, Edit, Clock, User, FileText, Send } from 'lucide-react';

export default function DocumentViewer({ document, onClose }) {
  const [activeTab, setActiveTab] = useState('preview');
  const [annotations, setAnnotations] = useState([
    { id: 1, page: 1, text: 'Review required for section 3.2', user: 'John Doe', date: '2024-12-05', resolved: false },
    { id: 2, page: 2, text: 'Update compliance date', user: 'Jane Smith', date: '2024-12-04', resolved: true }
  ]);
  const [comment, setComment] = useState('');
  const [versionHistory] = useState([
    { version: '2.1', date: '2024-12-01', user: 'Admin', changes: 'Updated compliance requirements' },
    { version: '2.0', date: '2024-11-15', user: 'Manager', changes: 'Major revision - regulatory changes' },
    { version: '1.5', date: '2024-10-20', user: 'Admin', changes: 'Minor corrections and formatting' }
  ]);

  const tabs = [
    { id: 'preview', label: 'Document Preview' },
    { id: 'annotations', label: 'Annotations' },
    { id: 'versions', label: 'Version History' },
    { id: 'metadata', label: 'Metadata' }
  ];

  const handleAddComment = () => {
    if (comment?.trim()) {
      const newAnnotation = {
        id: annotations?.length + 1,
        page: 1,
        text: comment,
        user: 'Current User',
        date: new Date()?.toISOString()?.split('T')?.[0],
        resolved: false
      };
      setAnnotations([...annotations, newAnnotation]);
      setComment('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-gray-900 truncate">{document?.title}</h2>
            <p className="text-sm text-gray-600 mt-1">{document?.regulatoryBody}</p>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <Download size={20} />
            </button>
            <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <Share2 size={20} />
            </button>
            <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <Edit size={20} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <nav className="flex gap-4">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab?.id
                    ? 'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab?.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'preview' && (
            <div className="space-y-4">
              <div className="bg-gray-100 rounded-lg p-8 min-h-[600px] flex items-center justify-center">
                <div className="text-center">
                  <FileText className="mx-auto text-gray-400 mb-4" size={64} />
                  <p className="text-gray-600 mb-2">Document Preview</p>
                  <p className="text-sm text-gray-500">High-resolution rendering with annotation capabilities</p>
                  <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    Open Full Document
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'annotations' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Collaborative Review:</strong> Add annotations and comments for team review and approval workflows
                </p>
              </div>
              
              <div className="space-y-3">
                {annotations?.map((annotation) => (
                  <div key={annotation?.id} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <User size={16} className="text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{annotation?.user}</p>
                          <p className="text-xs text-gray-500">Page {annotation?.page} • {annotation?.date}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        annotation?.resolved
                          ? 'bg-green-100 text-green-800' :'bg-yellow-100 text-yellow-800'
                      }`}>
                        {annotation?.resolved ? 'Resolved' : 'Open'}
                      </span>
                    </div>
                    <p className="text-gray-700 mt-2">{annotation?.text}</p>
                  </div>
                ))}
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add Annotation
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={comment}
                    onChange={(e) => setComment(e?.target?.value)}
                    placeholder="Enter your comment or annotation..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleAddComment}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Send size={16} />
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'versions' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Version Control:</strong> Track all document revisions with complete change history
                </p>
              </div>
              
              {versionHistory?.map((version, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <FileText size={20} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Version {version?.version}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                          <Clock size={12} />
                          <span>{version?.date}</span>
                          <span>•</span>
                          <span>by {version?.user}</span>
                        </div>
                      </div>
                    </div>
                    <button className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                      View Version
                    </button>
                  </div>
                  <p className="text-gray-700 mt-2">{version?.changes}</p>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'metadata' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Document Title</p>
                  <p className="font-medium text-gray-900">{document?.title}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Category</p>
                  <p className="font-medium text-gray-900">{document?.category}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Regulatory Body</p>
                  <p className="font-medium text-gray-900">{document?.regulatoryBody}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Status</p>
                  <p className="font-medium text-gray-900 capitalize">{document?.status}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Submission Date</p>
                  <p className="font-medium text-gray-900">{document?.submissionDate}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Expiry Date</p>
                  <p className="font-medium text-gray-900">{document?.expiryDate}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">File Size</p>
                  <p className="font-medium text-gray-900">{document?.fileSize}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Version</p>
                  <p className="font-medium text-gray-900">v{document?.version}</p>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-2">Tags</p>
                <div className="flex flex-wrap gap-2">
                  {document?.tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}